import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Typography, TableCell, Table, TableHead, TableRow, TableBody, IconButton, CircularProgress, Alert, Button
} from "@mui/material";
import { ThemeProvider } from '@mui/material/styles';
import '../../styles/HiringdashBoard.css';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, Unarchive as UnarchiveIcon } from '@mui/icons-material';
import Pagination from '../../utils/Pagination.js';
import JobDescriptionForm from './JobDescriptionForm.js';
import JDDetails from "../shared/JDDetails.js";
import NotificationDialog from '../shared/NotificationDialog';
import styled, { keyframes, css } from 'styled-components';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import useActivityLogger from '../../hooks/useActivityLogger';
import recruiterTheme, {
  FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW, PAGE_PADDING,
} from '../../styles/recruiterTheme';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const floatDot = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-3px); }
`;

// ─── Page Title ───────────────────────────────────────────────────────────────
const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 !important;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: ${COLORS.muted} !important;
    font-weight: 500 !important;
    margin: 4px 0 0 !important;
  }
`;

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${p => p.gradient || 'linear-gradient(135deg, #6750A4, #4F378B)'};
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  min-height: 96px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 24px ${p => p.shadow || 'rgba(103,80,164,0.28)'};
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  ${css`animation: ${fadeSlideIn} 0.4s ease both;`}
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 32px ${p => p.shadow || 'rgba(103,80,164,0.36)'};
  }
  &::after {
    content: '';
    position: absolute;
    top: -30px; right: -30px;
    width: 90px; height: 90px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  position: relative;
  z-index: 1;
`;

const StatNumber = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const StatLabel = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.25;
  opacity: 0.88;
  text-transform: uppercase;
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0.22;
  svg { font-size: 40px; }
`;

// ─── Cards ────────────────────────────────────────────────────────────────────
// White card around the table (and around the empty / error states).
const TableCard = styled.div`
  background: #fff;
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.lg}px;
  box-shadow: ${SHADOW.card};
  overflow: hidden;
  min-width: 0;
  ${css`animation: ${fadeSlideIn} 0.4s ease both;`}
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid ${COLORS.border};
`;

const CardTitle = styled.div`
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.cardTitle}px;
  line-height: 1.5;
  color: ${COLORS.primaryDeep};
`;

// ─── Table ────────────────────────────────────────────────────────────────────
const StyledTable = styled(Table)`
  && { border-collapse: separate; border-spacing: 0; }
  && tbody tr:last-child td { border-bottom: none !important; }
`;

const HeadCell = styled(TableCell)`
  && {
    background: linear-gradient(135deg, #4F378B 0%, #6750A4 100%);
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 12px 16px !important;
    white-space: nowrap;
    vertical-align: middle;
    border-bottom: none !important;
  }
`;

const BodyRow = styled(TableRow)`
  && {
    transition: background 0.16s;
    &:nth-child(even) td { background: #FDFBFF; }
    &:hover td { background: #F3EEF9 !important; }
  }
`;

const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    color: ${COLORS.ink} !important;
    padding: 12px 16px !important;
    vertical-align: middle;
    border-bottom: 1px solid #F0EAF9 !important;
  }
`;

// Two-line clamp for long free text (pair with title= for the full value).
const CellClamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  word-break: break-word;
  min-width: 200px;
`;

// ─── Status Chips (24px pills) ────────────────────────────────────────────────
const pillCss = `
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.overline}px;
  line-height: 1;
  height: 24px;
  padding: 0 10px;
  border-radius: ${RADIUS.pill}px;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  &::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
  }
`;

const ArchivedChip = styled.span`
  ${pillCss}
  background: linear-gradient(135deg, #EDE7F6, #D0BCFF);
  color: #4F378B;
  &::before { background: #7C5CBF; }
`;

const ExpiredChip = styled.span`
  ${pillCss}
  background: linear-gradient(135deg, #FDECEA, #FFCDD2);
  color: #B71C1C;
  &::before {
    background: #E53935;
    ${css`animation: ${floatDot} 2s ease-in-out infinite;`}
  }
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const LinkButton = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    text-transform: none !important;
    text-decoration: underline !important;
    text-underline-offset: 3px !important;
    min-width: 0 !important;
    padding: 4px 8px !important;
    margin-left: -8px;
    justify-content: flex-start;
    white-space: nowrap;
    line-height: 1.5;
    border-radius: ${RADIUS.sm}px !important;
    &:hover { background: rgba(103,80,164,0.06) !important; }
  }
`;

const GhostButton = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    text-transform: none !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 32px;
    padding: 0 12px !important;
    white-space: nowrap;
    &:hover { background: rgba(103,80,164,0.08) !important; }
  }
`;

// ─── Back Button ─────────────────────────────────────────────────────────────
const BackButton = styled(IconButton)`
  && {
    background: linear-gradient(135deg, #F3EEF9, #E8DEF8);
    border-radius: ${RADIUS.sm}px;
    width: 40px;
    height: 40px;
    transition: all 0.2s;
    flex-shrink: 0;
    &:hover {
      background: linear-gradient(135deg, #E8DEF8, #D0BCFF);
      transform: scale(1.08);
    }
    svg { color: #4F378B; font-size: 20px; }
  }
`;

// ─── Action Icon Button (32px, used in table rows) ────────────────────────────
const ActionIconButton = styled(IconButton)`
  && {
    border-radius: ${RADIUS.sm}px;
    width: 32px;
    height: 32px;
    padding: 0;
    transition: all 0.18s;
    background: ${p => p.variant === 'edit'
      ? 'linear-gradient(135deg, #EDE7F6, #D0BCFF)'
      : 'linear-gradient(135deg, #E8F5E9, #C8E6C9)'};
    color: ${p => p.variant === 'edit' ? '#4F378B' : '#2E7D32'} !important;
    &:hover {
      transform: scale(1.08);
      background: ${p => p.variant === 'edit'
        ? 'linear-gradient(135deg, #D0BCFF, #B39DDB)'
        : 'linear-gradient(135deg, #C8E6C9, #A5D6A7)'};
    }
    svg { font-size: 18px; }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
const getStoredUserEmail = () => {
  try {
    return JSON.parse(localStorage.getItem("rbotUser"))?.email || "";
  } catch {
    return "";
  }
};

// expiration_time is stored as UTC midnight of the picked date — show that date ("29 Oct 2026").
const formatExpiry = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

function ArchivedJdsPage({ hiringManagerEmail: hiringManagerEmailProp, onBack }) {
  const location = useLocation();
  const navigate = useNavigate();
  const hiringManagerEmail =
    hiringManagerEmailProp || location.state?.hiringManagerEmail || getStoredUserEmail();

  const [archivedJds, setArchivedJds] = useState([]);
  const [isEditJDOpen, setIsEditJDOpen] = useState(false);
  const [selectedJD, setSelectedJD] = useState(null);
  const [isViewJdDetailsOpen, setIsViewJdDetailsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [unarchivingId, setUnarchivingId] = useState(null);
  const [actionError, setActionError] = useState('');
  const requestIdRef = useRef(0);

  const deleteJDApiUrl = process.env.REACT_APP_DELETE_JD_API;
  const archivedJdsApiUrl = process.env.REACT_APP_ARCHIVE_JDS_API;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(archivedJds.length / itemsPerPage));
  // clamp when the list shrinks (e.g. last item on the last page was unarchived)
  const safePage = Math.min(currentPage, totalPages);

  const isExpired = (expirationTime) => new Date(expirationTime) < new Date();
  const expiredCount = archivedJds.filter((jd) => isExpired(jd.expiration_time)).length;
  const archivedCount = archivedJds.length - expiredCount;

  const handlePrevPage = () => setCurrentPage((p) => Math.max(Math.min(p, totalPages) - 1, 1));
  const handleNextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
  const displayedJds = archivedJds.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage);

  useEffect(() => {
    if (currentPage !== safePage) setCurrentPage(safePage);
  }, [currentPage, safePage]);

  const { log } = useActivityLogger(hiringManagerEmail, 'hiringManager');

  const fetchArchivedJds = useCallback(async ({ silent = false } = {}) => {
    const requestId = ++requestIdRef.current;
    if (!hiringManagerEmail) {
      setLoading(false);
      setFetchError("Couldn't determine the logged-in hiring manager. Please log in again.");
      return;
    }
    if (!silent) setLoading(true);
    setFetchError('');
    try {
      const response = await axios.get(`${archivedJdsApiUrl}/${encodeURIComponent(hiringManagerEmail)}`);
      if (requestId !== requestIdRef.current) return;   // a newer request superseded this one
      setArchivedJds(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error fetching archived Jds:", error);
      setFetchError(error.response?.data?.message || "Failed to load archived job descriptions.");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [archivedJdsApiUrl, hiringManagerEmail]);

  useEffect(() => { fetchArchivedJds(); }, [fetchArchivedJds]);

  const handleUnarchieveClick = async (jdId) => {
    if (unarchivingId) return;
    const jd = archivedJds.find(j => j._id === jdId);
    setUnarchivingId(jdId);
    setActionError('');
    try {
      const response = await axios.put(`${deleteJDApiUrl}/${encodeURIComponent(jdId)}`, { is_active: true });
      if (response.status < 200 || response.status >= 300) {
        throw new Error("Failed to Unarchive JD");
      }
      log('UNARCHIVE_JD', {
        jdId,
        targetName: jd?.jobRole,
        metadata: { jdID: jd?.jdID }
      });
      await fetchArchivedJds({ silent: true });
      setSnackbarMessage("Job Description successfully unarchived!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error Unarchiving JD:", error);
      setActionError(
        error.response?.data?.message ||
        `Failed to unarchive ${jd?.jdID || 'the job description'}. Please try again.`
      );
    } finally {
      setUnarchivingId(null);
    }
  };

  const handleViewJD = (jd = null) => {
    setIsViewJdDetailsOpen(true);
    setSelectedJD({ ...jd, userRole: 'hiringManager' });
  };

  const handleEditClick = (selectedJD) => {
    setIsEditJDOpen(true);
    setSelectedJD(selectedJD);
  };

  // navigate(-0) used to reload the whole app. Prefer the parent's onBack; otherwise go
  // back in history when this page was reached as a route, else to the dashboard.
  const handleBack = () => {
    if (typeof onBack === 'function') return onBack();
    const cameFromHistory = window.history.state?.idx > 0;
    if ((location.state?.fromEditJobDescription || location.pathname === '/ArchivedJdsPage') && cameFromHistory) {
      navigate(-1);
    } else {
      navigate('/hiring-dashboard', { state: { hiringManagerEmail } });
    }
  };

  const handleClose = () => setIsViewJdDetailsOpen(false);

  // On the dashboard this page is embedded in an already-padded container; only the
  // standalone /ArchivedJdsPage route adds its own page padding and canvas.
  const isStandalone = location.pathname.toLowerCase() === '/archivedjdspage';

  return (
    <ThemeProvider theme={recruiterTheme}>
      <Box sx={isStandalone
        ? { p: `${PAGE_PADDING}px`, minHeight: '100vh', background: COLORS.background, fontFamily: FONT_BODY }
        : { fontFamily: FONT_BODY }}
      >
      {!isEditJDOpen && !isViewJdDetailsOpen && (
        <>
          {/* ── Header ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <BackButton onClick={handleBack}>
              <ArrowBackIcon />
            </BackButton>
            <Box sx={{ minWidth: 0 }}>
              <PageTitle>Archived Job Descriptions</PageTitle>
              <PageSubtitle>Browse and manage your archived and expired job postings</PageSubtitle>
            </Box>
          </Box>

          {actionError && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: `${RADIUS.md}px` }} onClose={() => setActionError('')}>
              {actionError}
            </Alert>
          )}

          {loading ? (
            <TableCard>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress sx={{ color: '#6750A4' }} />
              </Box>
            </TableCard>
          ) : fetchError ? (
            <TableCard>
              <Box sx={{ textAlign: 'center', py: 5, px: 3 }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, color: COLORS.error, mb: hiringManagerEmail ? 1.5 : 0 }}>
                  {fetchError}
                </Typography>
                {hiringManagerEmail ? (
                  <GhostButton onClick={() => fetchArchivedJds()}>Retry</GhostButton>
                ) : null}
              </Box>
            </TableCard>
          ) : archivedJds.length === 0 ? (
            <TableCard>
              <Box sx={{ textAlign: 'center', py: 5, px: 3 }}>
                <Typography sx={{ fontFamily: FONT_BODY, color: COLORS.muted, fontSize: TYPE.body, lineHeight: 1.5 }}>
                  No archived job descriptions available.
                </Typography>
              </Box>
            </TableCard>
          ) : (
            <>
              {/* ── Stat Cards ── */}
              <StatGrid>
                <StatCard gradient="linear-gradient(135deg, #6750A4, #4F378B)" shadow="rgba(103,80,164,0.32)">
                  <StatText><StatNumber>{archivedCount}</StatNumber><StatLabel>Archived JDs</StatLabel></StatText>
                  <StatIcon><ArchiveOutlinedIcon /></StatIcon>
                </StatCard>
                <StatCard gradient="linear-gradient(135deg, #E53935, #B71C1C)" shadow="rgba(229,57,53,0.28)">
                  <StatText><StatNumber>{expiredCount}</StatNumber><StatLabel>Expired JDs</StatLabel></StatText>
                  <StatIcon><AccessTimeIcon /></StatIcon>
                </StatCard>
              </StatGrid>

              {/* ── Table ── */}
              <TableCard>
                <CardHeader>
                  <CardTitle>All Archived Records</CardTitle>
                </CardHeader>
                <TableScroll>
                  <StyledTable>
                    <TableHead>
                      <TableRow>
                        <HeadCell>JD ID</HeadCell>
                        <HeadCell>Job Status</HeadCell>
                        <HeadCell>Job Role</HeadCell>
                        <HeadCell>Expiration Time</HeadCell>
                        <HeadCell sx={{ textAlign: 'right' }}>Action</HeadCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedJds.map((jd, index) => (
                        <BodyRow key={jd._id || index}>
                          <BodyCell sx={{ whiteSpace: 'nowrap' }}>
                            <LinkButton onClick={() => handleViewJD(jd)}>
                              {jd.jdID}
                            </LinkButton>
                          </BodyCell>
                          <BodyCell sx={{ whiteSpace: 'nowrap' }}>
                            {isExpired(jd.expiration_time)
                              ? <ExpiredChip>Expired</ExpiredChip>
                              : <ArchivedChip>Archived</ArchivedChip>
                            }
                          </BodyCell>
                          <BodyCell sx={{ fontWeight: '600 !important' }}>
                            <CellClamp title={jd.jobRole || undefined}>{jd.jobRole}</CellClamp>
                          </BodyCell>
                          <BodyCell sx={{ color: `${COLORS.muted} !important`, whiteSpace: 'nowrap' }}>
                            {formatExpiry(jd.expiration_time)}
                          </BodyCell>
                          <BodyCell sx={{ textAlign: 'right' }}>
                            <Box sx={{ display: 'inline-flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                              {isExpired(jd.expiration_time) ? (
                                <ActionIconButton variant="edit" onClick={() => handleEditClick(jd)}>
                                  <EditIcon />
                                </ActionIconButton>
                              ) : (
                                <ActionIconButton
                                  variant="unarchive"
                                  disabled={Boolean(unarchivingId)}
                                  onClick={() => handleUnarchieveClick(jd._id)}
                                >
                                  {unarchivingId === jd._id
                                    ? <CircularProgress size={16} sx={{ color: '#2E7D32' }} />
                                    : <UnarchiveIcon />}
                                </ActionIconButton>
                              )}
                            </Box>
                          </BodyCell>
                        </BodyRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </TableScroll>
              </TableCard>

              {totalPages > 1 && (
                <Pagination
                  itemsPerPage={itemsPerPage}
                  currentPage={safePage}
                  totalPages={totalPages}
                  handlePrevPage={handlePrevPage}
                  handleNextPage={handleNextPage}
                />
              )}
            </>
          )}
        </>
      )}

      {isEditJDOpen && (
        <JobDescriptionForm
          jd={selectedJD}
          isEdit={isEditJDOpen}
          hiringManagerEmail={hiringManagerEmail}
          onClose={() => setIsEditJDOpen(false)}
          fetchJds={() => fetchArchivedJds({ silent: true })}
        />
      )}

      {isViewJdDetailsOpen && (
        <JDDetails jdDetails={selectedJD} userRole="hiringManager" onBack={handleClose} />
      )}

      <NotificationDialog
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={() => setSnackbarOpen(false)}
      />
      </Box>
    </ThemeProvider>
  );
}

export default ArchivedJdsPage;
